// src/pages/GTM/GTMPlaybook.jsx

import React,{useState,useEffect} from "react";
import axios from "../../utils/axiosInstance";
import { useIdea } from "../../context/IdeaContext";
import { useGTM } from "./context/GTMContext";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

import { Download,ArrowRight } from "lucide-react";

import ModuleHeader from "../../components/ModuleHeader";
import { buildMDIHPdfName } from "../../utils/pdfNaming";

import {
Box,
Grid,
Button,
CircularProgress,
Alert
} from "@mui/material";

import { adaptGTMText } from "./utils/gtmAdapter";
import { calculateGTMScore } from "./utils/gtmScoreCalculator";

import GTMStrategyCard from "./components/Strategy/GTMStrategyCard";
import ICPCard from "./components/Strategy/ICPCard";
import PositioningCard from "./components/Strategy/PositioningCard";

import AcquisitionTable from "./components/Execution/AcquisitionTable";
import RoadmapTimeline from "./components/Execution/RoadmapTimeline";
import ExperimentsBoard from "./components/Execution/ExperimentsBoard";

import MetricsPanel from "./components/Performance/MetricsPanel";
import RiskPanel from "./components/Performance/RiskPanel";
import GTMScoreCard from "./components/Performance/GTMScoreCard";

import ExecutionChecklist from "./components/Actions/ExecutionChecklist";


export default function GTMPlaybook(){

const navigate=useNavigate();

const {user}=useAuth();

const{

selectedIdea,
marketResearch,
mvpDetails,
businessModel,
aiTestingFeedback,
userId,
setUserId,
markModuleComplete

}=useIdea();

/* ⭐ GTM STARTUP RESOLVER (BusinessModel pattern – stable) */

const startupName =

selectedIdea?.ideaTitle ||

selectedIdea?.title ||

businessModel?.startupName ||

localStorage.getItem("activeIdeaTitle") ||

"Startup Idea";

const{

gtmPlan,
setGTMPlan,
loading,
setLoading,
error,
setError

}=useGTM();

const [loadingExport,setLoadingExport]=useState(false);


const[recordId,setRecordId]=useState(null);

const[structured,setStructured]=useState(null);

const[feedback,setFeedback]=useState("");

const[loadedFromDB,setLoadedFromDB]=useState(false);

const[showRefine,setShowRefine]=useState(false);


/* USER SYNC */

useEffect(()=>{

if(!userId && user?._id){

setUserId(user._id);

}

},[user,userId,setUserId]);

/* ⭐ Persist active startup */

useEffect(()=>{

if(selectedIdea?.ideaTitle){

localStorage.setItem(
"activeIdeaTitle",
selectedIdea.ideaTitle
);

}

},[selectedIdea]);

const activeUserId=

userId ||
user?._id ||
null;


/* LOAD LAST PLAN */

useEffect(()=>{

async function load(){

try{

const ideaId =
selectedIdea?.ideaId ||
businessModel?.ideaId;

if(!ideaId) return;

const r=await axios.get(
"/api/gtm/last",
{params:{ideaId}}
);

if(r.data?.gtmPlan){

const plan=r.data.gtmPlan;

setGTMPlan(plan);

setRecordId(r.data.recordId);

markModuleComplete("gtm",plan);

setLoadedFromDB(true);

}

}catch(err){

console.warn("Load GTM failed");

}

}

load();

},[selectedIdea,businessModel]);


/* STRUCTURE DATA — RESTORED WORKING VERSION */

useEffect(()=>{

if(!gtmPlan) return;

let rawPlan=

typeof gtmPlan==="string"
? gtmPlan
: gtmPlan?.gtmPlan ||
gtmPlan?.text ||
gtmPlan?.strategy ||
JSON.stringify(gtmPlan);


/* normalize headings only */

const normalizedPlan=

rawPlan

.replace(/Go.?To.?Market Strategy/gi,"STRATEGY")

.replace(/Ideal Customer Profile/gi,"ICP")

.replace(/Market Positioning/gi,"POSITIONING")

.replace(/Acquisition Channels/gi,"ACQUISITION CHANNELS")

.replace(/Growth Experiments/gi,"GROWTH EXPERIMENTS")

.replace(/Execution Roadmap/gi,"EXECUTION ROADMAP")

.replace(/Success Metrics/gi,"METRICS")

.replace(/KPIs/gi,"METRICS")

.replace(/Risks/gi,"RISKS")

.replace(/Founder Checklist/gi,"FOUNDER CHECKLIST");


/* ⭐ RESTORE OLD WORKING PARSER */

const structuredData = adaptGTMText(normalizedPlan);

const score = calculateGTMScore(structuredData);

setStructured({

...structuredData,

score:score || 0

});

},[gtmPlan]);


/* GENERATE */

async function handleGenerate(){

if(!activeUserId){

alert("Login required");

return;

}

try{

setLoading(true);

const ideaTitle =

startupName ||

selectedIdea?.ideaTitle ||

businessModel?.startupName ||

"Startup Idea";


const ideaSummary=`

${startupName}

Problem: ${selectedIdea?.problem || ""}

Target User: ${selectedIdea?.targetUser || ""}

Solution: ${
selectedIdea?.solution ||
selectedIdea?.description ||
""
}
`;


const payload={

ideaTitle,

userId:activeUserId,

ideaId:
selectedIdea?.ideaId ||
businessModel?.ideaId ||
null,

recordId:recordId || null,

ideaSummary,

founderCountry:
user?.country ||
"India",

targetMarket:
marketResearch?.location ||
"India",

marketResearch,

mvpDetails,

businessModel,

aiTestingFeedback

};


const {data}=await axios.post(
"/api/gtm/generate",
payload
);


const plan=data.gtmPlan;

setGTMPlan(plan);

setRecordId(data.recordId);

markModuleComplete("gtm",plan);

setLoadedFromDB(false);

setError(null);

}
catch(err){

setError(
err?.response?.data?.error ||
"Generation failed"
);

}
finally{

setLoading(false);

}

}


/* REFINE */

async function refine(){

if(feedback.length<8) return;

try{

setLoading(true);

const {data}=await axios.post(

"/api/gtm/refine",

{

planId:recordId,

feedback

}

);

setGTMPlan(data.refinedPlan);

}
catch{

setError("Refinement failed");

}
finally{

setLoading(false);

}

}


/* ----------------------------------------------------------
   DOWNLOAD PDF (Aligned with BusinessModel)
---------------------------------------------------------- */

async function downloadPDF(){

if(!gtmPlan) return;

const ideaId =

selectedIdea?.ideaId ||

businessModel?.ideaId ||

null;

if(!ideaId){

alert("Idea missing");

return;

}

try{

setLoadingExport(true);

const response = await axios.post(

"/api/pdf/gtm-strategy",

{
ideaId,
recordId
},

{
responseType:"blob"
}

);

const blob = new Blob(
[response.data],
{type:"application/pdf"}
);

const url =
window.URL.createObjectURL(blob);

const link =
document.createElement("a");

link.href = url;

link.download =
buildMDIHPdfName(
startupName,
"GTM_Playbook"
);

document.body.appendChild(link);

link.click();

link.remove();

}
catch(err){

console.error(
"GTM PDF download failed:",
err
);

alert("Failed to download PDF");

}
finally{

setLoadingExport(false);

}

}


/* EMPTY */

if(!structured && !loading){

return(

<div className="bg-slate-50 min-h-screen">

<div className="max-w-6xl mx-auto px-6 py-10">

<ModuleHeader

title="Go-To-Market"

subtitle="Build your launch and growth execution strategy."

currentModule="gtm"

startup={startupName}   // ⭐ ADD THIS

isLoadedFromDB={loadedFromDB}

/>

{/* ACTIVE IDEA */}

        <div className="text-sm text-gray-500 border-b border-slate-200 pb-4 mb-6">

        <b>Startup:</b> {startupName}

        </div>
<Button

variant="contained"

onClick={handleGenerate}

className="mt-6"

>

Generate GTM Strategy

</Button>

</div>

</div>

);

}


/* UI */

return(

<div className="bg-slate-50 min-h-screen">

<div className="max-w-6xl mx-auto px-6 py-10 space-y-8">

<ModuleHeader

title="Go-To-Market"

subtitle="Build your launch and growth execution strategy."

currentModule="gtm"

startup={startupName}   // ⭐ ADD THIS

isLoadedFromDB={loadedFromDB}

/>

{/* ACTIVE IDEA */}

<div className="text-sm text-gray-500 border-b border-slate-200 pb-4 mb-6">

<b>Startup:</b> {startupName}

</div>

<div className="flex flex-wrap gap-3">

<button

onClick={handleGenerate}

disabled={loading}

className="bg-blue-600 text-white px-7 py-2.5 rounded-lg text-sm font-semibold"

>

{loading ? (
<>
<CircularProgress size={16} style={{color:"white"}}/>
Generating...
</>
):"Generate Strategy"}

</button>

<button

onClick={()=>navigate(-1)}

className="text-blue-600 text-sm"

>

← Back

</button>

</div>

{loading && <CircularProgress/>}

{error && <Alert severity="error">{error}</Alert>}

{structured &&(

<Box className="bg-white border rounded-xl p-6">

<div className="flex justify-between mb-4">

<div>

<h3 className="text-2xl font-bold text-slate-800">
GTM Strategy Report
</h3>

AI generated go-to-market execution blueprint

</div>

<div className={`px-3 py-1 rounded text-xs ${
loadedFromDB
?"bg-amber-100 text-amber-800"
:"bg-green-100 text-green-800"
}`}>

{loadedFromDB
?"Loaded from previous session"
:"Newly generated"}

</div>

</div>

<button

onClick={downloadPDF}
            disabled={loadingExport}
            className="mt-2 bg-green-600 hover:bg-blue-700
                        text-white px-6 py-3 rounded-lg font-semibold
                        inline-flex items-center gap-2 transition
                        disabled:opacity-50"
            >

            <Download size={18}/>

            {loadingExport
            ? "Preparing PDF..."
            : "Download GTM Strategy Report"}


</button>

<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">

<div className="flex justify-between items-center">

<div>

<div className="text-sm text-blue-700 font-semibold">
GTM Readiness Score
</div>

<div className="text-xs text-blue-700 mt-1">

Execution maturity:
{
structured.score>=8
? " Strong"
: structured.score>=5
? " Moderate"
: " Early"
}

</div>

</div>

<div className="text-sm text-blue-800">

{
structured.score>=8
? "Ready for scale"
: structured.score>=5
? "Validation stage"
: "Early stage"
}

</div>

</div>

</div>

<Grid container spacing={3}>

{/* STRATEGIC FOUNDATION */}

<Grid item xs={12}>
<h2 className="text-xl font-semibold text-slate-800 mb-2">
Strategic Foundation
</h2>
</Grid>

<Grid item xs={12} md={4}>
<GTMStrategyCard data={structured}/>
</Grid>

<Grid item xs={12} md={4}>
<ICPCard data={structured}/>
</Grid>

<Grid item xs={12} md={4}>
<PositioningCard data={structured}/>
</Grid>


{/* DISTRIBUTION */}

<Grid item xs={12}>
<h2 className="text-xl font-semibold text-slate-800 mt-6 mb-2">
Distribution Engine
</h2>
</Grid>

<Grid item xs={12}>
<AcquisitionTable data={structured}/>
</Grid>


{/* EXECUTION */}

<Grid item xs={12}>
<h2 className="text-xl font-semibold text-slate-800 mt-6 mb-2">
Execution Plan
</h2>
</Grid>

<Grid item xs={12} md={6}>
<RoadmapTimeline data={structured}/>
</Grid>

<Grid item xs={12} md={6}>
<ExperimentsBoard data={structured}/>
</Grid>


{/* PERFORMANCE */}

<Grid item xs={12}>
<h2 className="text-xl font-semibold text-slate-800 mt-6 mb-2">
Performance Tracking
</h2>
</Grid>

<Grid item xs={12} md={4}>
<MetricsPanel data={structured}/>
</Grid>

<Grid item xs={12} md={4}>
<RiskPanel data={structured}/>
</Grid>

<Grid item xs={12} md={4}>
<GTMScoreCard score={structured.score}/>
</Grid>


{/* FOUNDER ACTION */}

<Grid item xs={12}>
<h2 className="text-xl font-semibold text-slate-800 mt-6 mb-2">
Founder Execution Actions
</h2>
</Grid>

<Grid item xs={12}>
<ExecutionChecklist data={structured}/>
</Grid>


{/* IMPROVE PLAN */}

<Grid item xs={12}>

<div className="border-t pt-8 mt-6">

<div className="flex justify-between items-start">

<div className="flex flex-col gap-1">

<div className="text-lg font-semibold text-slate-800">
Refine GTM Strategy
</div>

<div className="text-sm text-slate-500">
Ask AI to improve specific areas of your plan
</div>

</div>

<button

onClick={()=>setShowRefine(!showRefine)}

className="bg-green-600 text-white px-6 py-2.5 rounded-lg text-sm font-semibold h-fit"

>

Improve Plan

</button>

</div>


{showRefine &&(

<div className="mt-5">

<textarea

value={feedback}

onChange={(e)=>setFeedback(e.target.value)}

placeholder="Example: Improve acquisition channels or pricing strategy..."

className="w-full border border-slate-300 rounded-lg p-4 text-sm min-h-[110px]"

/>

<div className="flex gap-3 mt-3">

<button

onClick={refine}

disabled={loading}

className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-semibold"

>

Apply Improvements

</button>

<button

onClick={()=>setShowRefine(false)}

className="text-slate-500 text-sm"

>

Cancel

</button>

</div>

</div>

)}

</div>

</Grid>


{/* NEXT STEP */}

<Grid item xs={12}>

<div className="border-t pt-8">

<div className="bg-slate-50 border border-slate-200 rounded-xl p-6">

<div className="text-xl font-semibold text-blue-900">

Ready to Build Your Investor Pitch?

</div>

<div className="text-slate-600 mt-2 max-w-2xl">

Convert your validated go-to-market strategy into a structured investor pitch deck with problem, solution, traction and growth story.

</div>

<button

onClick={()=>navigate(
"/pitch-deck",
{
state:{
gtmPlan,
ideaSummary:startupName,
businessModel,
marketResearch,
mvpDetails
}
}
)}

className="mt-5 bg-blue-600 hover:bg-blue-700 text-white px-7 py-3 rounded-lg text-sm font-semibold flex items-center gap-2 transition"

>

Generate Pitch Deck

<ArrowRight size={16}/>

</button>

</div>

</div>

</Grid>

</Grid>


</Box>

)}

</div>

</div>

);

}